// @givmo/board — one meeting's minutes: the draft, the approval, the signatures, the clock.
//
// ══ THE DRAFT IS IDEMPOTENT, AND RE-TAKES THE SNAPSHOT ═════════════════════════════════════════
//
// `PUT …/minutes` opens the draft, and called again WHILE IT IS A DRAFT it re-takes the snapshot of
// the structured record — so a motion recorded after the draft was opened is in it. That is why the
// button says "Refresh from what's recorded" rather than "Save": the officer's own sections are
// preserved, and what changes is the snapshot underneath them.
//
// The record's `payload` carries that snapshot and its `body` carries the SECRETARY'S OWN sections.
// They are kept apart because they are separate authors, and a surface that merged them would make it
// unanswerable afterwards which words were whose.
//
// ══ THE DOORS INTO APPROVED, AND THE ONE THIS PAGE OFFERS (CBG-RC11, D-CBG-26) ═════════════════
//
// The minutes have three doors, sharing one implementation (`board_minutes_filing.approve_minutes_record`):
//
//   * **the recording officer, directly** — `POST …/minutes/approval`. The approver limb is the
//     officer's ACCOUNT, which is §3.4's own sanctioned phrasing ("as approved by your [officer] on
//     [date]").
//   * **a carried motion at a later meeting** — `POST …/motions/{id}/minutes-approval`. The approver
//     is the SEAT the organization records as having PRESIDED, with `manual_on_behalf` capture. Naming the
//     recording officer there would repeat the false record CBG-RC4 rejected for consents (they did
//     not approve; they wrote down that the board did). The motion is recorded on that meeting's
//     record, where its vote is.
//   * **a written consent of the directors** (CBG-RC9, below).
//   * **the signatures of the directors who were in a closed session** (CBG-RC10, D-CBG-24), for a
//     closed session's own minutes where the board's closed-session rule names that way: one press
//     asks them (echoing the read's bytes), each signs in their own session or an officer records it,
//     and the last signature on the current bytes approves them, server-side, through the same one
//     function. The page reads where the request stands off `approval_signatures` and decides none
//     of it (`minutes-signatures-decode.ts`).
//
// Each board's own rule decides which one its minutes go through, and the server serves the answer on
// the minutes read (`approval_way`, backend `board_minutes.md` §A6) and refuses every other door at its
// start (§A4). So this page offers ONE approval action, the one the rule allows, with one plain line
// and the detail behind "When and why" (`minutes-rule-decode.ts`'s `approvalPlan`); it never works the
// way out itself, and with no `approval_way` served it offers none.
//
// ══ THE DIRECTORS' DOOR: A WRITTEN CONSENT THAT NAMES THESE MINUTES (CBG-RC9, D-CBG-22) ══════════
//
// "Approve by written consent" starts a consent, prefilled from the backend's neutral form, that
// names these minutes by their fingerprint; when its last signature lands, the minutes are approved
// through the same one function, as they are when it went out (contract `board-minutes-by-consent.md`).
// The page says where that stands — out for signature, drafted, or executed without approving them —
// from the read's own `approval_consents`, and an approval it made reads as the directors' act, with
// the way to the consent. While one is out the minutes stay editable, with a warning (Q8): a change
// means the consent won't approve them, and it says so when it's filed.
//
// ══ THE ECHO AND THE STALENESS GATE ════════════════════════════════════════════════════════════
//
// The approval echoes `content_hash` FROM THE READ THIS PAGE RENDERED — see `minutes-decode.ts`'s
// {@link approvalEcho} for why that binding is the semantic and not an implementation detail. Nothing
// here computes a hash, and nothing re-reads to obtain one.
//
// Both refusals arrive as 409 and are rendered with the BACKEND'S OWN SENTENCE, because they need
// different actions and its message is the one that says which:
//
//   * the bytes changed since the approver read them → re-read and approve that;
//   * the snapshot has fallen behind the motions table → refresh the draft, then approve.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useNavigate, useParams, useSearch } from '../../board-routing';

import { extractList } from '@givmo/api-client/list';
import {
  firstErrorCode,
  isGivmoError,
  type OrgBoardMemberOut,
  type OrgBoardRecordOut,
  type BoardMinutesOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Select,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { MeetingFrame } from '../meetings/meeting-frame';
import { FieldNote } from '../../components/field-note';
import { ReadOnlyDate } from '../../components/read-only-date';
import { recordApprovalLine, recordDateWording } from '../records/records-decode';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { isCommitteeOnlySeat, sortSeats } from '../roster/roster-decode';
import { displayDate, displayInstant } from '../../lib/display';
import { ContemporaneityBlock } from './contemporaneity-block';
import {
  extractSegments,
  isOpenSession,
  meetingDayOf,
  seatPickerLabel,
  seatsServingOn,
  segmentHeading,
} from '../in-meeting/in-meeting-decode';
import {
  approvalEcho,
  extractAwaitedSignatures,
  extractSignatures,
  hasDraft,
  isMinutesAbsence,
  isTheRecord,
  minutesDate,
  minutesStateLabel,
  minutesStateTone,
  minutesSignatureLine,
  minutesSignerLine,
  type AwaitedSignature,
} from './minutes-decode';
import { useBoardOperations } from '../../board-runtime';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { closedSessionWords } from '../in-meeting/closed-session-words';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import {
  capacityFieldValue,
  capacityOptions,
  chosenCapacity,
  currentTitles,
} from '../roster/officer-roles';
import {
  approvedByConsentId,
  minutesApprovalConsents,
  minutesConsentLine,
  type MinutesConsentLine,
} from '../consents/minutes-consent-decode';
import { minutesConsentWords as rc9 } from '../consents/minutes-consent-words';
import { minutesSetsWords as several } from '../consents/minutes-sets-words';
import { MinutesConsentDialog } from './minutes-consent-dialog';
import { HowThisWorks } from '../../components/fine-print';
import {
  approvalPlan,
  minutesApprovalWay,
  type ApprovalAction,
  type ApprovalPlan,
  type ExplainedLine,
} from './minutes-rule-decode';
import { minutesRuleWords as rc11 } from './minutes-rule-words';
import { APPROVE_MINUTES_SEARCH } from '../in-meeting/motion-approval';
import {
  approvalSignaturesRequest,
  approvedBySignatures,
  signedToApprove,
  unsignedSigners,
  withdrawnRequests,
  type ApprovalSignaturesRequest,
  type AskedSigner,
  type WithdrawnRequest,
} from './minutes-signatures-decode';
import { minutesSignaturesWords as rc10 } from './minutes-signatures-words';
import { minutesAskWords as ask } from './minutes-ask-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { meetingCommitteeName } from '../meetings/meetings-decode';
import { consentDeliveryWords as circ } from '../consents/consent-delivery-words';
import { seatsStillUnjoined } from '../consents/consents-decode';
import { captureMethodLabel, sentenceMoment } from '../records/records-decode';
import { recordPersonWords as fix49 } from '../records/record-person-words';

/** An inline link inside a line of text. */
const INLINE_LINK =
  'font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** The picker's sentinel for "the meeting's own minutes" — a Select needs a string, not null. */
const MEETING_SCOPE = 'meeting';

/** Each approval action's testid: RC9's and the officer's kept as they were (an e2e contract). */
const ACTION_TEST_IDS: Record<ApprovalAction['kind'], string> = {
  consent: 'minutes-approve-by-consent',
  meeting: 'minutes-approve-at-meeting',
  officer: 'minutes-approve',
  // CBG-RC10: the frames' own id (the design harness's `minutes-ask-signatures`).
  ask: 'minutes-ask-signatures',
};

/** A line of the approval area's quiet type. */
const APPROVAL_LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

export function MinutesPage() {
  const operations = useBoardOperations();
  const { meetingId } = useParams({ from: '/authenticated/meetings/$meetingId/minutes' });
  const queryClient = useQueryClient();
  const { orgRef, isReady } = useBoardHost();
  const org = orgRef ?? '';
  const enabled = isReady && orgRef !== null;
  // Three strings, three kinds of act: drafting and refreshing the minutes and writing their sections
  // (`board_record:write`); approving them and recording a signature on a director's behalf, the
  // record-producing acts (`board_record:approve`); and the meeting-level reminder
  // (`board_meeting:write`).
  const canDraft = useBoardPermission('board_record:write');
  const canApprove = useBoardPermission('board_record:approve');
  const canRemind = useBoardPermission('board_meeting:write');
  // CBG-RC9 — starting a consent that names these minutes is its own operation, under the string
  // that circulates (`board_consent:manage`, contract §7).
  const canStartConsent = useBoardPermission('board_consent:manage');
  // CBG-RC11 — recording a motion that approves these minutes at a meeting is a motion, so it is the
  // meeting-record string (`board_meeting:write`), as `create_motion` is; its vote stays Owner-only.
  const canRecordMotion = canRemind;
  const navigate = useNavigate();

  // ⚠ THE ECHO IS CAPTURED INTO STATE AT DIALOG-OPEN, NOT DERIVED FROM THE LIVE QUERY.
  //
  // `approvalEcho(minutes)` reads whatever the query holds AT RENDER TIME. A dialog is open across
  // many renders, and this query is refetched by events the officer never sees — `refetchOnReconnect`
  // is on by default, `staleTime` is 30s, and any sibling mutation invalidates the key. So deriving
  // the echo per render means a wifi blip mid-dialog silently swaps BOTH the content behind the
  // dialog AND the hash the confirm button will send: the officer clicks approve, the new hash
  // matches the new bytes, the backend is satisfied, and they have approved a version they never
  // read. No 409 fires, because nothing is stale — which is precisely the failure the echo exists to
  // make impossible.
  //
  // Capturing it at open makes a mid-dialog change produce the DESIGNED refusal instead: the stale
  // hash goes out, the backend refuses it, and the officer is told to re-read. The hash IS the
  // identity of the bytes, so capturing it captures what was on screen when they decided.
  // ══ WHICH MINUTES THIS PAGE IS SHOWING ═══════════════════════════════════════════════════════
  //
  // A meeting with a closed session has TWO sets of minutes with two audiences — that is what the
  // plan's "executive-session records stored separately with independent permissions" means in the
  // schema, and `board_meeting_segment.minutes_record_id` is where a closed session's own minutes
  // hang. `null` is the meeting's minutes; a segment id is that session's.
  //
  // ⚠ ABSENCE SEMANTICS DECIDE THE PICKER'S CONTENTS FOR FREE. The sessions read is already scoped —
  // a closed session this officer was not part of never arrives — so the picker offers exactly the
  // scopes they may see, with no membership check of its own and nothing anywhere that counts what
  // was withheld. An officer entitled to nothing sees no picker, which is the same thing a meeting
  // with no closed sessions shows.
  //
  // `?session=<segment id>` opens a closed session's own minutes directly — the minute book's link to
  // a closed session's minutes carries it (CBG-FIX8's consumer: such minutes are edited and approved
  // here, never on the record's own page). It is only a STARTING choice, and it counts only once the
  // sessions read shows that session: one this reader cannot see never arrives there, so the page
  // falls back to the meeting's own minutes — and until that read lands no minutes read goes out, so
  // the address alone never sends a request for a session nobody here was shown.
  const search = useSearch();
  const requestedSession = typeof search.session === 'string' ? search.session : null;
  // `undefined` until the officer picks a scope themselves; from then on, their pick.
  const [pickedScope, setPickedScope] = useState<string | null | undefined>(undefined);
  // The minutes' DATE rides the same capture (CBG-FIX6): the dialog shows the date the read said the
  // approval would seal, not whatever a refetch says later.
  const [approving, setApproving] = useState<{
    contentHash: string;
    date: string | null | undefined;
  } | null>(null);
  const [signing, setSigning] = useState<{ contentHash: string } | null>(null);
  // CBG-RC10: an officer's entry of one asked director's signature, its echo captured at open like
  // every approval echo here; and the withdrawal of the request.
  const [signingToApprove, setSigningToApprove] = useState<{ contentHash: string } | null>(null);
  const [withdrawingRequest, setWithdrawingRequest] = useState(false);
  const [editingSections, setEditingSections] = useState(false);
  const [startingConsent, setStartingConsent] = useState(false);

  const meetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
  });

  const segmentsQuery = useQuery({
    ...operations.officerListBoardMeetingSegmentsOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
    select: extractSegments,
  });
  // Only CLOSED sessions file their own minutes; an open session's content is the meeting's.
  const closedSessions = (segmentsQuery.data ?? []).filter((segment) => !isOpenSession(segment));
  const scopeSegmentId: string | null =
    pickedScope !== undefined
      ? pickedScope
      : requestedSession !== null &&
          closedSessions.some((segment) => segment.id === requestedSession)
        ? requestedSession
        : null;
  // With a session in the address, the minutes read waits for the sessions read to settle it.
  const scopeSettled =
    requestedSession === null || pickedScope !== undefined || !segmentsQuery.isPending;

  const minutesOptions = operations.officerGetBoardMinutesOptions({
    path: { org_ref: org, meeting_id: meetingId },
    // Absent for the meeting's own minutes; present for a closed session's. The query key carries it,
    // so switching scope is a different cache entry rather than a mutation of one.
    query: scopeSegmentId === null ? {} : { segment_id: scopeSegmentId },
  });
  const minutesQuery = useQuery({
    ...minutesOptions,
    enabled: enabled && scopeSettled,
    retry: false,
  });

  // The WHOLE roster (CBG-FIX41): "Which seat signed" offers everyone serving now and everyone who
  // served on the meeting's day, former seats included — `seatsServingOn`, which puts no start bound
  // on a serving seat, so a newly elected Secretary can sign the previous meeting's minutes.
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({
      path: { org_ref: org },
      query: { include_removed: true },
    }),
    enabled,
    select: (body: unknown) => sortSeats(extractList<OrgBoardMemberOut>(body, 'data')),
  });

  const minutes = minutesQuery.data ?? null;
  const roster = rosterQuery.data ?? [];
  // CBG-RC10-MAIL: "Hasn’t joined Board yet" is present tense, so it is said only of an asked
  // director whose seat is still unjoined on the live roster (the consent page's own rule).
  const stillUnjoined = seatsStillUnjoined(rosterQuery.isSuccess ? roster : null);
  // CBG-RC10-WITHDRAWN: the requests withdrawn on these minutes, newest first.
  const withdrawn = withdrawnRequests(minutes);
  // CBG-FIX70 — minutes an officer approved alone say who, as their record's page does: the minutes
  // read carries no approver, so the page reads their record and prints its approval line
  // (`recordApprovalLine`, CBG-FIX49's rule: the person as they were that day, in the reader's zone).
  // The other three ways name their act from the minutes read itself. The record page's own key, so
  // the two pages share one read. Minutes whose approving consent's record is no longer kept read no
  // consent here either, and take the record's line too, which says so outright (CBG-FIX39).
  const approvedByTheOfficer =
    Boolean(minutes?.approved_at && minutes.record_id) &&
    approvedByConsentId(minutes) === null &&
    approvedBySignatures(minutes) === null &&
    !minutes?.approved_at_meeting_id;
  const approvalRecordQuery = useQuery({
    queryKey: ['board-record', orgRef, minutes?.record_id ?? ''] as const,
    enabled: enabled && approvedByTheOfficer,
    queryFn: async (): Promise<OrgBoardRecordOut> => {
      const { data } = await operations.officerGetBoardRecord({
        path: { org_ref: org, record_id: minutes?.record_id ?? '' },
        throwOnError: true,
      });
      return data;
    },
  });
  const officerApprovalLine =
    approvedByTheOfficer && approvalRecordQuery.data
      ? recordApprovalLine(approvalRecordQuery.data)
      : null;
  // Every act here also changes what two other pages read (CBG-UX8-WRITE, -BOOK): the meetings list,
  // whose row says "Write minutes" until a draft opens, and the Minute book, whose "In progress"
  // lists the drafts. The console holds a read 30 s and never refetches on focus, so each act
  // refreshes both beside the minutes, or a meeting just drafted would still ask for its minutes.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: minutesOptions.queryKey }),
      queryClient.invalidateQueries({
        queryKey: operations.officerListBoardMeetingsOptions({ path: { org_ref: org } }).queryKey,
      }),
      queryClient.invalidateQueries({ queryKey: ['board-records', orgRef] }),
    ]);

  const openOrRefreshDraft = useMutation({
    mutationFn: async () => {
      await operations.officerWriteBoardMinutesDraft({
        path: { org_ref: org, meeting_id: meetingId },
        // Only the scope: opening the draft with no title/body re-takes the snapshot and leaves the
        // secretary's own sections untouched. The sections are edited through their own dialog.
        // ⚠ `segment_id` rides the BODY here and the QUERY on the read/approval/signature routes —
        // that asymmetry is the backend's, not a mistake to "fix" on one side.
        body: scopeSegmentId === null ? {} : { segment_id: scopeSegmentId },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Brought up to date with what is recorded.');
      void invalidate();
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t open the draft. Please try again.',
      );
    },
  });

  const sendReminder = useMutation({
    mutationFn: async () => {
      await operations.officerSendBoardMinutesReminder({
        path: { org_ref: org, meeting_id: meetingId },
        body: {},
        throwOnError: true,
      });
    },
    onSuccess: () => {
      // Nothing schedules a reminder — sending one is an act the organization performs (§3.6 keeps
      // elections and defaults with the organization), so the confirmation names it as something the
      // officer just did rather than something the product will keep doing.
      toast.success('Reminder sent.');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t send that. Please try again.',
      );
    },
  });

  // CBG-RC10 (backend §S4): one press asks the directors who were in this closed session to sign to
  // approve its minutes, echoing the bytes THIS page rendered (no dialog first, §S16 C13). The
  // server decides who is asked and refuses with its own sentence — nobody to ask, a request already
  // out, minutes that moved — which the toast shows verbatim.
  const askForSignatures = useMutation({
    mutationFn: async (contentHash: string) => {
      await operations.officerAskBoardMinutesApprovalSignatures({
        path: { org_ref: org, meeting_id: meetingId },
        query: { segment_id: scopeSegmentId ?? '' },
        body: { content_hash: contentHash },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(rc10.asked);
      void invalidate();
    },
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : 'We couldn’t do that. Please try again.');
      void invalidate();
    },
  });

  if (meetingQuery.isPending && enabled) {
    return (
      <PageShell title="Minutes" testId="page-minutes">
        <div className="flex flex-col gap-12" data-testid="minutes-loading">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </PageShell>
    );
  }

  const meeting = meetingQuery.data ?? null;
  if (meetingQuery.isError || meeting === null) {
    return (
      <PageShell title="Minutes" testId="page-minutes">
        <Card testId="minutes-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this meeting.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void meetingQuery.refetch()}
              testId="minutes-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  // A 404 is the ordinary "no draft yet" state; anything else is a failure to LOAD, and the two get
  // different screens — see `isMinutesAbsence`.
  const loadFailed = minutesQuery.isError && !isMinutesAbsence(minutesQuery.error);
  const started = hasDraft(minutes);
  const settled = isTheRecord(minutes);
  const signatures = extractSignatures(minutes);
  // CBG-YR3-ROLES layer 3 (`l3-awaiting`): whose signature these minutes await under the board's
  // adopted duties — empty with none adopted, so the card reads as it did before them.
  const awaited = extractAwaitedSignatures(minutes);
  // ⚠ Taken from THE OBJECT THIS PAGE RENDERED FROM. Every act below echoes this value; none of them
  // re-reads to obtain one. See `minutes-decode.ts` → `approvalEcho`.
  const echo = approvalEcho(minutes);
  // CBG-FIX6 — minutes are approved only with their date. When the read SAYS they have none (`null`,
  // not a read that said nothing) the approval is not offered at all: the page says why instead of
  // offering a button that leads to a dialog with no approval in it.
  const approvable = minutesDate(minutes) !== null;
  // CBG-RC9 — every consent naming these minutes, newest first; `null` when the read carried none
  // at all (a backend without the door), and then nothing here offers it.
  const approvalConsents = minutesApprovalConsents(minutes);
  const consentLine = minutesConsentLine(approvalConsents, settled);
  const approvedByConsent = approvedByConsentId(minutes);
  // CBG-RC11 — the one way these minutes are approved, as the server serves it, and what the page
  // shows for it (§A9's table). Only for dated draft minutes: approved ones say how they were, and
  // undated ones say why they can't be yet. A closed session's own minutes take a consent too now
  // (RC9's refusal lifted, §A7), named by public facts only.
  // CBG-RC10 — the request out on these draft minutes for the signatures of the directors who were
  // in the closed session, as the server serves it, and, once approved, whether signatures did it.
  const signaturesRequest = settled ? null : approvalSignaturesRequest(minutes);
  const approvedBySigs = approvedBySignatures(minutes);
  // CBG-YR6-APPROVE (frame c5): a committee's meeting's minutes say the committee where the board's
  // say the board — read off the meeting's own body, fixed when it was scheduled.
  const meetingCommittee = meetingCommitteeName(meeting);
  const plan =
    !settled && approvable
      ? approvalPlan(minutesApprovalWay(minutes), {
          consentPending: consentLine?.kind === 'pending',
          signaturesOut: signaturesRequest !== null,
          onAClosedSession: scopeSegmentId !== null,
        })
      : null;
  const closedSegment =
    scopeSegmentId === null
      ? null
      : (closedSessions.find((segment) => segment.id === scopeSegmentId) ?? null);
  // Each action is offered only to a holder of its own string, and the consent door only when the
  // read says which consents are out (RC9: a list not served is not "none").
  const offered = (plan?.actions ?? []).filter((action) =>
    action.kind === 'consent'
      ? canStartConsent && approvalConsents !== null
      : action.kind === 'meeting'
        ? canRecordMotion && minutes?.record_id !== undefined && minutes.record_id !== null
        : action.kind === 'ask'
          ? // The ask is an approval's start (`board_record:approve`), on a closed session's own
            // minutes only, and it echoes the read's bytes: none served, no act.
            canApprove && scopeSegmentId !== null
          : canApprove,
  );
  const runAction = (action: ApprovalAction) => {
    if (action.kind === 'consent') {
      setStartingConsent(true);
    } else if (action.kind === 'meeting') {
      // The meeting's record, with a motion approving these minutes ready to fill in (the motion
      // dialog's approval mode; C5). The minutes are named by their record and their meeting.
      void navigate({
        to: '/meetings/$meetingId/record',
        params: { meetingId: action.meeting.id },
        search: {
          [APPROVE_MINUTES_SEARCH.record]: minutes?.record_id ?? '',
          [APPROVE_MINUTES_SEARCH.from]: meetingId,
          ...(scopeSegmentId === null ? {} : { [APPROVE_MINUTES_SEARCH.session]: scopeSegmentId }),
        },
      });
    } else if (action.kind === 'ask') {
      if (echo !== null) askForSignatures.mutate(echo);
    } else if (echo !== null) {
      setApproving({ contentHash: echo, date: minutesDate(minutes) });
    }
  };

  // The meeting's shared title block and tabs (CBG-UX1): this page is the "Minutes" tab, at its own
  // address; the meeting record is the neighbouring tab.
  return (
    <MeetingFrame meeting={meeting} tab="minutes" testId="page-minutes">
      {closedSessions.length > 0 ? (
        <Card testId="minutes-scope">
          <CardBody className="flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                Which minutes
              </h2>
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {/* Says what the separation IS, without claiming anything about what it achieves. */}
                {closedSessionWords.whichMinutesLead}
              </p>
            </div>
            <Select
              label="Showing"
              appearance="saas"
              value={scopeSegmentId ?? MEETING_SCOPE}
              onValueChange={(next) =>
                next !== null && setPickedScope(next === MEETING_SCOPE ? null : next)
              }
              options={[
                { value: MEETING_SCOPE, label: 'The meeting’s minutes' },
                ...closedSessions.map((segment) => ({
                  value: segment.id,
                  label: segmentHeading(segment),
                })),
              ]}
              testId="minutes-scope-select"
            />
          </CardBody>
        </Card>
      ) : null}

      {loadFailed ? (
        <Card testId="minutes-load-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t load the minutes for this meeting. This is not the same as there being
              none — nothing here has been changed, and no draft has been started.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void minutesQuery.refetch()}
              testId="minutes-load-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : !started ? (
        <Card testId="minutes-not-started">
          <CardBody className="flex flex-col items-start gap-12">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              No minutes drafted yet
            </h2>
            {/* Drafting is `board_record:write`: a reader is told the fact, not how to act on it. */}
            {canDraft ? (
              <>
                <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  Opening a draft takes what you have recorded about this meeting — who was there,
                  the sessions, every motion and vote — and puts it into a set of minutes you can
                  add your own sections to.
                </p>
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={openOrRefreshDraft.isPending}
                  onClick={() => openOrRefreshDraft.mutate()}
                  testId="minutes-open-draft"
                >
                  Draft the minutes
                </Button>
              </>
            ) : null}
          </CardBody>
        </Card>
      ) : (
        <>
          <Card testId="minutes-summary">
            <CardBody className="flex flex-col gap-12">
              <div className="flex flex-wrap items-center justify-between gap-12">
                <div className="flex flex-wrap items-center gap-8">
                  <Badge tone={minutesStateTone(minutes?.state)} testId="minutes-state">
                    {minutesStateLabel(minutes?.state)}
                  </Badge>
                  {minutes?.confidentiality === 'executive_session' ? (
                    <Badge tone="warning">Executive session</Badge>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-10">
                  {/* Each act is offered only to a holder of its own string: drafting is
                      `board_record:write`, approving and signing on a director's behalf are
                      `board_record:approve`. */}
                  {!settled ? (
                    <>
                      {canDraft ? (
                        <>
                          <Button
                            appearance="saas"
                            tone="secondary"
                            loading={openOrRefreshDraft.isPending}
                            onClick={() => openOrRefreshDraft.mutate()}
                            testId="minutes-refresh"
                          >
                            Refresh from what’s recorded
                          </Button>
                          <Button
                            appearance="saas"
                            tone="secondary"
                            onClick={() => setEditingSections(true)}
                            testId="minutes-edit-sections"
                          >
                            Your sections
                          </Button>
                        </>
                      ) : null}
                      {/* CBG-RC11: the one approval action the board's rule allows (two after a
                          withdrawn consent, the board's own way last and leading). */}
                      {offered.map((action, index) => (
                        <Button
                          key={action.kind}
                          appearance="saas"
                          tone={index === offered.length - 1 ? 'primary' : 'secondary'}
                          // The officer's approval and the ask echo the read's address: none served,
                          // no act.
                          disabled={
                            (action.kind === 'officer' || action.kind === 'ask') && echo === null
                          }
                          loading={action.kind === 'ask' && askForSignatures.isPending}
                          onClick={() => runAction(action)}
                          testId={ACTION_TEST_IDS[action.kind]}
                        >
                          {action.kind === 'consent'
                            ? rc9.approveByConsent
                            : action.kind === 'meeting'
                              ? rc11.approveAtMeeting(action.day)
                              : action.kind === 'ask'
                                ? rc10.askForTheirSignatures
                                : 'Approve them'}
                        </Button>
                      ))}
                      {/* CBG-RC10 (frame c2): while the request is out, an officer records an asked
                          director's signature given somewhere else — never once the session's
                          recorded seats changed, when none can count (§S7). */}
                      {canApprove &&
                      signaturesRequest !== null &&
                      !signaturesRequest.sessionChanged &&
                      unsignedSigners(signaturesRequest).length > 0 ? (
                        <Button
                          appearance="saas"
                          tone="secondary"
                          disabled={echo === null}
                          onClick={() =>
                            echo !== null && setSigningToApprove({ contentHash: echo })
                          }
                          testId="minutes-record-approval-signature"
                        >
                          Record a signature
                        </Button>
                      ) : null}
                    </>
                  ) : canApprove ? (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      onClick={() => echo !== null && setSigning({ contentHash: echo })}
                      testId="minutes-sign-on-behalf"
                    >
                      Record a signature
                    </Button>
                  ) : null}
                </div>
              </div>

              {canApprove && !settled && !approvable ? (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                  data-testid="minutes-no-date"
                >
                  These minutes have no date recorded, so they can’t be approved yet. Refreshing
                  them takes the meeting’s date.
                </p>
              ) : null}

              {plan !== null ? <ApprovalLines plan={plan} /> : null}

              {signaturesRequest !== null ? (
                <SignaturesRequestLine
                  request={signaturesRequest}
                  committee={meetingCommittee}
                  canWithdraw={canApprove}
                  onWithdraw={() => setWithdrawingRequest(true)}
                />
              ) : null}

              {consentLine !== null ? (
                <ConsentLine
                  line={consentLine}
                  settled={settled}
                  onAClosedSession={scopeSegmentId !== null}
                />
              ) : null}

              {minutes?.approved_at && (!approvedByTheOfficer || officerApprovalLine !== null) ? (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                  data-testid="minutes-approved-line"
                >
                  {/* §3.4's own sanctioned phrasing. It names WHO and WHEN and stops — it does not say
                      the minutes are valid, final, or that anything is satisfied by them. */}
                  {approvedBySigs !== null ? (
                    // CBG-RC10: the act of the directors who signed, never the one whose signature
                    // came last (CBG-UX3's rule), as the backend's approval statement says it — a
                    // committee's, its members' (CBG-YR6).
                    meetingCommittee !== null ? (
                      cm.signaturesApprovedLine(
                        approvedBySigs.signedCount,
                        displayDate(minutes.approved_at),
                      )
                    ) : (
                      rc10.approvedLine(
                        approvedBySigs.signedCount,
                        displayDate(minutes.approved_at),
                      )
                    )
                  ) : approvedByConsent !== null ? (
                    // CBG-RC9: the directors' act, never the one seat whose signature completed it
                    // (CBG-UX3's rule), with the way to the consent — a committee's, its members'.
                    <>
                      {meetingCommittee !== null
                        ? cm.approvedByConsentLine(
                            meetingCommittee,
                            displayDate(minutes.approved_at),
                          )
                        : rc9.approvedLine(minutes.approved_at)}{' '}
                      <Link
                        to="/consents/$consentId"
                        params={{ consentId: approvedByConsent }}
                        className={INLINE_LINK}
                        data-testid="minutes-approved-consent-link"
                      >
                        {rc9.openConsent}
                      </Link>
                    </>
                  ) : minutes.approved_at_meeting_id ? (
                    <>
                      Approved on {displayInstant(minutes.approved_at)}, by a motion carried at a
                      later meeting.
                    </>
                  ) : (
                    // CBG-FIX70: the officer's own door (`POST …/minutes/approval`), the one way left:
                    // the record's own line, naming who approved them, so the line says how for every
                    // way and the chip beside it need not.
                    officerApprovalLine
                  )}
                </p>
              ) : null}

              {echo !== null ? (
                // The WHOLE fingerprint (CBG-UX4), as the record page shows it: a shortened one
                // cannot be compared against the approval or a later copy.
                <div className="flex flex-col gap-6" data-testid="minutes-content-address">
                  <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    Their fingerprint is
                  </span>
                  <Fingerprint value={echo} testId="minutes-fingerprint" />
                </div>
              ) : null}

              {minutes?.record_id ? (
                <Link
                  to="/records/$recordId"
                  params={{ recordId: minutes.record_id }}
                  className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                  data-testid="minutes-book-link"
                >
                  In the minute book
                </Link>
              ) : null}
            </CardBody>
          </Card>

          <ContemporaneityBlock clock={minutes?.contemporaneity ?? null} />

          {/* ⚠ The reminder route takes NO `segment_id` — it is a meeting-level act, so it is offered
              only on the meeting's own minutes rather than sending a request whose scope the backend
              would silently widen. CBG-FIX79: never for a meeting called off, which has no line-8
              date and which the backend refuses to remind the board about. */}
          {!settled && scopeSegmentId === null && canRemind && meeting.status === 'scheduled' ? (
            <Card testId="minutes-reminder-card">
              <CardBody className="flex flex-col items-start gap-8">
                <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                  {/* CBG-YR6-APPROVE (frame c5): a committee's minutes remind its members. */}
                  {meetingCommittee !== null ? cm.remindTitle : 'Remind the board'}
                </h2>
                <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {/* CBG-YR6-OUTSIDE (frame o15): the roster now holds people the board's minutes
                      don't go to, so the board's reminder says whom it reaches. */}
                  {meetingCommittee !== null ? cm.remindLine(meetingCommittee) : co.boardRemindLine}
                </p>
                <Button
                  appearance="saas"
                  tone="secondary"
                  loading={sendReminder.isPending}
                  onClick={() => sendReminder.mutate()}
                  testId="minutes-remind"
                >
                  Send a reminder
                </Button>
              </CardBody>
            </Card>
          ) : null}

          <Card testId="minutes-content">
            <CardBody className="flex flex-col gap-12">
              <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                {minutes?.title ?? 'Minutes'}
              </h2>

              {minutes?.body ? (
                <div className="flex flex-col gap-4" data-testid="minutes-body">
                  <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                    Your sections
                  </span>
                  <p className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                    {minutes.body}
                  </p>
                </div>
              ) : null}

              {minutes?.structured_record ? (
                <div className="flex flex-col gap-4" data-testid="minutes-structured">
                  <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                    What was recorded about the meeting
                  </span>
                  {/* The backend's own deterministic rendering of the snapshot, shown verbatim. The
                      PAYLOAD it renders rides inside the content hash (the render itself is outside
                      it, re-derived on every read), so a paraphrase here would show an approver
                      something other than what they are approving. */}
                  <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {minutes.structured_record}
                  </p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card testId="minutes-signatures">
            <CardBody className="flex flex-col gap-12">
              <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                Signatures
              </h2>
              {!settled && signaturesRequest !== null ? (
                // CBG-RC10 (frame c2): the directors asked, where each one's signature stands on the
                // CURRENT bytes, then who wasn't asked and why — all as the request recorded them.
                <AskedSignersList
                  request={signaturesRequest}
                  committee={meetingCommittee}
                  stillUnjoined={stillUnjoined}
                />
              ) : !settled && plan?.actions.some((action) => action.kind === 'ask') ? (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                  data-testid="minutes-signatures-before-ask"
                >
                  {/* CBG-RC10-MAIL: the approved line, then what the one press will also do. */}
                  {meetingCommittee !== null
                    ? cm.signaturesBeforeAsk
                    : rc10.signaturesBeforeAsk}{' '}
                  {ask.askEmails}
                </p>
              ) : !settled ? (
                <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {/* Order matters and is stated: a signature follows the board's approval, so there
                      is nothing to sign yet rather than a missing feature. A committee's minutes
                      follow the committee's (CBG-YR6-APPROVE, frame c5). */}
                  {meetingCommittee !== null
                    ? cm.signatureFollows
                    : 'A signature follows the board’s approval, so there is nothing to sign yet.'}
                </p>
              ) : signatures.length === 0 && awaited.length === 0 ? (
                <p
                  className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
                  data-testid="minutes-signatures-empty"
                >
                  No signatures recorded.
                </p>
              ) : signatures.length === 0 ? null : (
                <ul className="flex flex-col gap-8" data-testid="minutes-signatures-list">
                  {signatures.map((signature, index) => (
                    <li
                      key={`${signature.board_member_id ?? 'seat'}-${index}`}
                      className="flex flex-col gap-2 rounded-lg border border-border-subtle p-12"
                      data-testid={`minutes-signature-${index}`}
                    >
                      {/* Who signed first, by name, and their email of record after it — the way
                          the consent page shows a seat (CBG-UX4). */}
                      <span
                        className="font-sans text-s13 font-w600 tracking-givmo text-text-strong"
                        data-testid={`minutes-signature-name-${index}`}
                      >
                        {/* CBG-YR3-ROLES layer 2 (`l2-signatures`): the capacity the signature was
                            given in, in the words it was recorded with ("Maren Lindqvist, as
                            Secretary") — never the title's current name. */}
                        {minutesSignerLine(signature)}
                      </span>
                      {signature.identity_assertion ? (
                        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                          {signature.identity_assertion}
                        </span>
                      ) : null}
                      {/* The consent log's words for how it was made — one function for both
                          (`minutes-decode.ts`); the two capture classes never share them. */}
                      <span
                        className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                        data-testid={`minutes-signature-capture-${index}`}
                      >
                        {signedToApprove(signature) ? (
                          // CBG-RC10 (frame c2): one of the signatures that approved them, first in
                          // the list, as the server orders it.
                          rc10.signedToApprove(
                            sentenceMoment(signature.occurred_at),
                            captureMethodLabel(signature.capture_method),
                          )
                        ) : (
                          <>
                            {minutesSignatureLine(signature)}
                            {signature.occurred_at
                              ? ` · ${displayInstant(signature.occurred_at)}`
                              : null}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {/* Layer 3: whom Board asks, by the adopted duty — the seat and the title it signs in.
                  It decides whom Board asks, never who may sign: anyone on the roster still can. */}
              {settled
                ? awaited.map((entry) => (
                    <p
                      key={entry.boardMemberId}
                      className="font-sans text-s14 font-w500 tracking-givmo text-text-strong wrap-anywhere"
                      data-testid={`minutes-signatures-awaiting-${entry.boardMemberId}`}
                    >
                      {l3.awaitingSignature(entry.capacityLabel, entry.name)}
                    </p>
                  ))
                : null}
              {/* CBG-RC10-WITHDRAWN (§S18.3): every request withdrawn on these minutes stays
                  legible, before the approval and after it. */}
              {withdrawn.length > 0 ? <EarlierRequestsList requests={withdrawn} /> : null}
            </CardBody>
          </Card>
        </>
      )}

      {editingSections && minutes !== null ? (
        <SectionsDialog
          orgRef={org}
          meetingId={meetingId}
          minutes={minutes}
          segmentId={scopeSegmentId}
          onClose={() => setEditingSections(false)}
          onSaved={() => {
            setEditingSections(false);
            void invalidate();
          }}
        />
      ) : null}

      {approving !== null ? (
        <ApprovalDialog
          orgRef={org}
          meetingId={meetingId}
          // Captured when the dialog opened — see the state declaration. Deliberately NOT `echo`,
          // which is the live value and would track a mid-dialog refetch.
          contentHash={approving.contentHash}
          minutesDate={approving.date}
          segmentId={scopeSegmentId}
          onClose={() => setApproving(null)}
          onApproved={() => {
            setApproving(null);
            void invalidate();
          }}
        />
      ) : null}

      {startingConsent && minutes?.record_id ? (
        <MinutesConsentDialog
          orgRef={org}
          recordId={minutes.record_id}
          meetingTitle={meeting.title}
          meetingDate={minutesDate(minutes) ?? null}
          closedSession={
            closedSegment === null
              ? null
              : { segmentId: closedSegment.id, purposeLabel: closedSegment.purpose_label ?? null }
          }
          // CBG-RC9-MULTI: the other sets of the meeting the consent would name with these, placed
          // in the meeting's own order of its closed sessions.
          alsoNames={minutesApprovalWay(minutes)?.consentNamesWith ?? []}
          sessionOrder={closedSessions.map((segment) => segment.id)}
          // CBG-FIX104: a committee's minutes take the committee's reader line.
          committee={meetingCommittee}
          onClose={() => setStartingConsent(false)}
          onCreated={(consentId) => {
            setStartingConsent(false);
            void invalidate();
            void navigate({ to: '/consents/$consentId', params: { consentId } });
          }}
        />
      ) : null}

      {signingToApprove !== null && signaturesRequest !== null && scopeSegmentId !== null ? (
        <ApprovalSignatureOnBehalfDialog
          orgRef={org}
          meetingId={meetingId}
          segmentId={scopeSegmentId}
          committee={meetingCommittee}
          // Captured when the dialog opened, as every echo here.
          contentHash={signingToApprove.contentHash}
          signers={unsignedSigners(signaturesRequest)}
          onClose={() => setSigningToApprove(null)}
          onRecorded={() => {
            setSigningToApprove(null);
            void invalidate();
          }}
        />
      ) : null}

      {withdrawingRequest && scopeSegmentId !== null ? (
        <WithdrawSignaturesDialog
          orgRef={org}
          meetingId={meetingId}
          segmentId={scopeSegmentId}
          onClose={() => setWithdrawingRequest(false)}
          onWithdrawn={() => {
            setWithdrawingRequest(false);
            void invalidate();
          }}
        />
      ) : null}

      {signing !== null ? (
        <OnBehalfSignatureDialog
          orgRef={org}
          meetingId={meetingId}
          contentHash={signing.contentHash}
          segmentId={scopeSegmentId}
          // The board's minutes: never someone who isn't on the board (CBG-YR6-OUTSIDE, edge 12); a
          // committee's minutes are its members' to sign, such a seat among them.
          roster={seatsServingOn(
            meetingCommittee === null
              ? roster.filter((seat) => !isCommitteeOnlySeat(seat))
              : roster,
            meetingDayOf(meeting),
          )}
          awaited={scopeSegmentId === null ? awaited : []}
          onClose={() => setSigning(null)}
          onSigned={() => {
            setSigning(null);
            void invalidate();
          }}
        />
      ) : null}
    </MeetingFrame>
  );
}

/**
 * CBG-RC11 — what the minutes page says about approving draft minutes under their one way (§A9):
 * the way's plain line with its "When and why", the directors the minutes record as conflicted
 * (§A8), the meeting way after a withdrawn consent (§A5) and, with nowhere to record a motion, where
 * to; and a motion approving them that is already recorded (C6), with the way to its meeting.
 */
function ApprovalLines({ plan }: { plan: ApprovalPlan }) {
  return (
    <>
      {plan.line !== null ? <ExplainedLineRow line={plan.line} testId="minutes-way" /> : null}
      {plan.recusal !== null ? (
        <p className={APPROVAL_LINE} data-testid="minutes-recusal">
          {plan.recusal}
        </p>
      ) : null}
      {plan.meetingWay !== null ? (
        <ExplainedLineRow line={plan.meetingWay} testId="minutes-meeting-way" />
      ) : null}
      {plan.noNextMeeting !== null ? (
        <p className={APPROVAL_LINE} data-testid="minutes-no-next-meeting">
          {plan.noNextMeeting}
        </p>
      ) : null}
      {plan.pendingMotion !== null ? (
        <p className={APPROVAL_LINE} data-testid="minutes-pending-motion">
          {rc11.pendingMotionLine(plan.pendingMotion.day)}{' '}
          <Link
            to="/meetings/$meetingId/record"
            params={{ meetingId: plan.pendingMotion.meeting.id }}
            className={INLINE_LINK}
            data-testid="minutes-pending-motion-link"
          >
            {rc11.openThatMeeting}
          </Link>
        </p>
      ) : null}
    </>
  );
}

/** One plain line, and its detail a click away (CBG-UX1's "say less"). */
function ExplainedLineRow({ line, testId }: { line: ExplainedLine; testId: string }) {
  return (
    <p className={APPROVAL_LINE} data-testid={testId}>
      {line.text} <HowThisWorks lines={line.why} label={rc11.whenAndWhy} testId={`${testId}-why`} />
    </p>
  );
}

/**
 * The one line the minutes say about a consent that names them (CBG-RC9): out for signature, with
 * Q8's warning while they can still change; drafted; or executed without approving them, with why.
 * Each leads to the consent. One out that names other sets of the meeting too says which (CBG-RC9-MULTI,
 * frame `m3`).
 */
function ConsentLine({
  line,
  settled,
  onAClosedSession,
}: {
  line: MinutesConsentLine;
  settled: boolean;
  onAClosedSession: boolean;
}) {
  const link = (
    <Link
      to="/consents/$consentId"
      params={{ consentId: line.consentId }}
      className={INLINE_LINK}
      data-testid="minutes-consent-link"
    >
      {rc9.openConsent}
    </Link>
  );
  const words =
    line.kind === 'pending'
      ? line.alsoNames.length > 0
        ? several.pendingLine(line.alsoNames, onAClosedSession)
        : rc9.pendingLine
      : line.kind === 'drafted'
        ? rc9.draftedLine
        : rc9.refusedLine(line.outcome);
  return (
    <div className="flex flex-col gap-4" data-testid={`minutes-consent-${line.kind}`}>
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {words} {link}
      </p>
      {line.kind === 'pending' && !settled ? (
        <FieldNote testId="minutes-consent-pending-note">{rc9.pendingNote}</FieldNote>
      ) : null}
    </div>
  );
}

function SectionsDialog({
  orgRef,
  meetingId,
  minutes,
  segmentId,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  minutes: BoardMinutesOut;
  /** `null` for the meeting's minutes; a segment id for a closed session's own. */
  segmentId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const [title, setTitle] = useState(minutes.title ?? '');
  const [body, setBody] = useState(minutes.body ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerWriteBoardMinutesDraft({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: {
          title: title.trim().length > 0 ? title.trim() : null,
          body: body.trim().length > 0 ? body.trim() : null,
          segment_id: segmentId,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Saved.');
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label="Your sections of the minutes"
        className="flex flex-col gap-16"
        testId="minutes-sections-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Your sections
        </Dialog.Title>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Says the two authors are kept apart, which is why editing here cannot touch the record
              of what the board did. */}
          What you write here sits beside what was recorded about the meeting — it does not replace
          it. The attendance, sessions and motions come from what you entered on the other screen.
        </p>

        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          testId="minutes-title"
        />

        <Textarea
          label="Your sections"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={10}
          testId="minutes-body-field"
        />

        {error !== null ? (
          <ErrorMessage testId="minutes-sections-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="minutes-sections-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="minutes-sections-save"
          >
            Save
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function ApprovalDialog({
  orgRef,
  meetingId,
  contentHash,
  minutesDate: date,
  segmentId,
  onClose,
  onApproved,
}: {
  orgRef: string;
  meetingId: string;
  /** ⚠ The hash from the read that rendered what the approver read. Never refetched here. */
  contentHash: string;
  /**
   * The date the approval will seal, from that same read (CBG-FIX6) — see `minutesDate` for what
   * its three answers mean. Shown, never edited here: minutes take their meeting's day.
   */
  minutesDate: string | null | undefined;
  /** `null` for the meeting's minutes; a segment id for a closed session's own. */
  segmentId: string | null;
  onClose: () => void;
  onApproved: () => void;
}) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: async () => {
      await operations.officerApproveBoardMinutes({
        path: { org_ref: orgRef, meeting_id: meetingId },
        query: segmentId === null ? {} : { segment_id: segmentId },
        // ⚠ THE SAME-READ BINDING. `contentHash` came in as a prop from the object the page rendered
        // the minutes from; this dialog does not read the minutes and does not compute a hash. An
        // echo obtained here would name bytes nobody looked at, and the approval's whole evidentiary
        // value is that it names the exact artifact a human approved.
        body: { content_hash: contentHash, note: note.trim() || null },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded as approved.');
      onApproved();
    },
    onError: (caught) => {
      // The backend's own sentence, verbatim. The two 409s need different actions from the officer —
      // re-read and approve THAT, versus refresh the draft first — and its message is the one that
      // says which. A generic "please try again" would send them round the same failing loop.
      setError(
        isGivmoError(caught)
          ? caught.message
          : 'We couldn’t record the approval. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !approve.isPending && onClose()}>
      <Dialog.Content
        aria-label="Approve these minutes"
        className="flex flex-col gap-16"
        testId="minutes-approval-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Approve these minutes?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          This records that you, as the officer signed in, approved these minutes today. They become
          part of the minute book, and everything recorded about the meeting is fixed from that
          point — a later change is recorded as a correction.
        </p>

        {typeof date === 'string' ? (
          <ReadOnlyDate
            label={recordDateWording('minutes').label}
            date={date}
            testId="minutes-approval-date"
          />
        ) : null}

        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid="minutes-approval-record-not-decisions"
        >
          {/* CBG-RC11: the board's rule decides the door, so this dialog no longer names another
              (C18); it says what approving minutes is. */}
          {rc11.approvalRecordNotDecisions}
        </p>

        <TextField
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId="minutes-approval-note"
        />

        <FieldNote testId="minutes-approval-echo">
          Approving records the exact text shown on this page. If it has changed since you opened
          it, this will be refused and you can read the current version.
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="minutes-approval-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={approve.isPending}
            onClick={onClose}
            testId="minutes-approval-cancel"
          >
            Not yet
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={approve.isPending}
            onClick={() => {
              setError(null);
              approve.mutate();
            }}
            testId="minutes-approval-confirm"
          >
            Approve them
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function OnBehalfSignatureDialog({
  orgRef,
  meetingId,
  contentHash,
  segmentId,
  roster,
  awaited,
  onClose,
  onSigned,
}: {
  orgRef: string;
  meetingId: string;
  contentHash: string;
  /** `null` for the meeting's minutes; a segment id for a closed session's own. */
  segmentId: string | null;
  roster: readonly OrgBoardMemberOut[];
  /** Whose signature the minutes await under the adopted duties (layer 3); empty without them. */
  awaited: readonly AwaitedSignature[];
  onClose: () => void;
  onSigned: () => void;
}) {
  const operations = useBoardOperations();
  // CBG-YR3-ROLES layer 3 (`l3-onbehalf-suggested`): with duties adopted, the dialog starts on the
  // first seat the minutes await — one this dialog offers — and the title its duty signs in, and says
  // the suggestion came from the duties. Only a suggestion: the officer may choose any seat.
  const suggestion = awaited.find((entry) =>
    roster.some((seat) => seat.id === entry.boardMemberId),
  );
  const [seatId, setSeatId] = useState<string | null>(suggestion?.boardMemberId ?? null);
  // CBG-YR3-ROLES layer 2: "Signing as" offers only the titles the chosen seat holds now, and starts
  // EMPTY until the board adopts duties (question 9); with them, on the suggested seat's duty title.
  const [capacityId, setCapacityId] = useState<string | null>(suggestion?.capacityTitleId ?? null);
  const [provenanceNote, setProvenanceNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const chosenSeat = roster.find((seat) => seat.id === seatId) ?? null;
  const capacities = chosenSeat === null ? [] : currentTitles(chosenSeat);
  const capacity = chosenCapacity(capacityId, capacities);

  const sign = useMutation({
    mutationFn: async () => {
      if (seatId === null) return;
      await operations.officerSignBoardMinutesOnBehalf({
        path: { org_ref: orgRef, meeting_id: meetingId },
        query: segmentId === null ? {} : { segment_id: segmentId },
        body: {
          board_member_id: seatId,
          content_hash: contentHash,
          provenance_note: provenanceNote.trim(),
          ...(capacity !== null ? { capacity_title_id: capacity } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Signature recorded.');
      onSigned();
    },
    onError: (caught) => {
      // The title chosen as "Signing as" left that seat since the roster was read: the choice goes
      // back to empty, and the backend's own sentence says why.
      if (isGivmoError(caught) && firstErrorCode(caught) === 'board_signature_capacity_not_held') {
        setCapacityId(null);
      }
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !sign.isPending && onClose()}>
      <Dialog.Content
        aria-label={roles.onBehalfName}
        className="flex flex-col gap-16"
        testId="minutes-onbehalf-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record a signature
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Never presented as the person having acted in their own session — CBG-RC4's rule. The
              provenance note is REQUIRED by the backend for exactly this reason, and the copy says
              what it is for rather than just marking it required. The words say "someone", and the
              list below names whose seat: for the board's minutes, the seats on the board that day,
              director or not (CBG-YR3-ROLES); for a committee's, every seat that served then
              (CBG-FIX104, row 2.2). */}
          {roles.onBehalfBody}
        </p>

        <Select
          label="Which seat signed"
          appearance="saas"
          placeholder="Choose a seat"
          value={seatId}
          onValueChange={(next) => {
            setSeatId(next);
            // A capacity belongs to the seat that signed: a different seat starts empty again — the
            // suggested seat, on its duty title.
            setCapacityId(
              suggestion !== undefined && next === suggestion.boardMemberId
                ? suggestion.capacityTitleId
                : null,
            );
          }}
          options={roster.map((seat) => ({ value: seat.id, label: seatPickerLabel(seat) }))}
          testId="minutes-onbehalf-seat"
        />
        {suggestion !== undefined && seatId === suggestion.boardMemberId ? (
          <FieldNote testId="minutes-onbehalf-suggested">
            {l3.onBehalfSuggested(suggestion.capacityLabel)}
          </FieldNote>
        ) : null}

        {capacities.length > 0 ? (
          <>
            <Select
              label={l2.signingAs}
              appearance="saas"
              placeholder={l2.chooseCapacity}
              value={capacityFieldValue(capacityId, capacities)}
              onValueChange={(next) => setCapacityId(next)}
              options={capacityOptions(capacities)}
              testId="minutes-onbehalf-capacity"
            />
            <FieldNote testId="minutes-onbehalf-capacity-note">{l2.signingAsNote}</FieldNote>
          </>
        ) : null}

        <Textarea
          label="How you know they signed"
          value={provenanceNote}
          onChange={(event) => setProvenanceNote(event.target.value)}
          rows={3}
          testId="minutes-onbehalf-provenance"
        />
        <FieldNote>
          Required. Say where the signature came from — a signed page you hold, an email, a call you
          took. This is what makes the entry evidence years later.
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="minutes-onbehalf-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={sign.isPending}
            onClick={onClose}
            testId="minutes-onbehalf-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={sign.isPending}
            disabled={seatId === null || provenanceNote.trim().length === 0 || sign.isPending}
            onClick={() => {
              setError(null);
              sign.mutate();
            }}
            testId="minutes-onbehalf-save"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * CBG-RC10 (frame c2): the request's line — "Out for the signatures of the 4 directors who were there.
 * 2 of 4 have signed." with its small warning — or, once the session's recorded seats changed since
 * the ask, why it can't complete; and "Withdraw the request", a quiet action beside it, to a holder of
 * the string that asks (`board_record:approve`).
 */
function SignaturesRequestLine({
  request,
  committee,
  canWithdraw,
  onWithdraw,
}: {
  request: ApprovalSignaturesRequest;
  /** CBG-YR6: a committee meeting's committee — its members were there — or null for the board. */
  committee: string | null;
  canWithdraw: boolean;
  onWithdraw: () => void;
}) {
  const withdraw = canWithdraw ? (
    <button
      type="button"
      className={INLINE_LINK}
      onClick={onWithdraw}
      data-testid="minutes-withdraw-signatures"
    >
      {rc10.withdrawTheRequest}
    </button>
  ) : null;
  if (request.sessionChanged) {
    return (
      <p className={APPROVAL_LINE} data-testid="minutes-signatures-session-changed">
        {rc10.sessionChanged} {withdraw}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4" data-testid="minutes-signatures-out">
      <p className={APPROVAL_LINE}>
        {committee !== null
          ? cm.signaturesOutLine(request.signerCount, request.signedCount)
          : rc10.outLine(request.signerCount, request.signedCount)}{' '}
        {withdraw}
      </p>
      <FieldNote testId="minutes-signatures-out-note">{rc10.outWarning}</FieldNote>
    </div>
  );
}

/** How one asked director's signature stands, in the card's one quiet line (frame c2). */
function askedSignerLine(signer: AskedSigner): string {
  const when = sentenceMoment(signer.signedAt);
  if (signer.signed === 'current') {
    return rc10.signedToApprove(when, captureMethodLabel(signer.captureMethod));
  }
  if (signer.signed === 'earlier_version') return rc10.signedEarlierVersion(when);
  return rc10.notSignedYet;
}

/**
 * CBG-RC10-MAIL (§S17.8): what the ask's email did for one asked director, in CBG-OPS1-CIRC-WEB's
 * approved words from their one definition. "The email didn’t go out" is said only while the
 * director still has to sign the current version, since it asks the officer to act; nothing at all
 * for an ask made before the email existed.
 */
function AskedSignerDelivery({
  signer,
  index,
  stillUnjoined,
}: {
  signer: AskedSigner;
  index: number;
  stillUnjoined: ReadonlySet<string>;
}) {
  const outcome = signer.delivery;
  if (outcome === null) return null;
  const testId = `minutes-approval-signer-delivery-${index}`;
  if (outcome.state === 'not_sent') {
    if (signer.signed === 'current') return null;
    return (
      <span
        className="font-sans text-s12 font-w600 tracking-givmo text-warning-700"
        data-testid={testId}
      >
        {circ.notSent}
      </span>
    );
  }
  return (
    <span
      className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
      data-testid={testId}
    >
      {outcome.state === 'sent'
        ? circ.emailedLine(
            displayDate(outcome.sentAt),
            outcome.unjoinedThen && stillUnjoined.has(signer.boardMemberId),
          )
        : circ.notConfirmed}
    </span>
  );
}

/**
 * CBG-RC10-WITHDRAWN (§S18.3, not drawn): the withdrawn requests for signatures, newest first, each
 * with who asked and when, and who withdrew it, when and why.
 */
function EarlierRequestsList({ requests }: { requests: readonly WithdrawnRequest[] }) {
  return (
    <div
      className="flex flex-col gap-6 border-t border-border-subtle pt-12"
      data-testid="minutes-earlier-requests"
    >
      <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
        {ask.earlierRequests}
      </span>
      <ul className="flex flex-col gap-6">
        {requests.map((request) => (
          <li
            key={request.requestId}
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary wrap-anywhere"
            data-testid={`minutes-earlier-request-${request.requestId}`}
          >
            {ask.withdrawnRequest({
              askedBy: request.requestedByName,
              askedOn: displayDate(request.requestedAt),
              withdrawnBy: request.withdrawnByName,
              withdrawnOn: displayDate(request.withdrawnAt),
              reason: request.reason,
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * CBG-RC10 (frame c2): the directors the request asked, each with where their signature stands on the
 * current bytes and (CBG-RC10-MAIL) what the ask's email did for them, then "Not asked: …" naming who
 * was in the session and wasn't asked, and why.
 */
function AskedSignersList({
  request,
  committee,
  stillUnjoined,
}: {
  request: ApprovalSignaturesRequest;
  /** CBG-YR6: a committee meeting's committee — whose voting members were asked — or null. */
  committee: string | null;
  stillUnjoined: ReadonlySet<string>;
}) {
  return (
    <div className="flex flex-col gap-12" data-testid="minutes-approval-signatures">
      <ul className="flex flex-col gap-8">
        {request.signers.map((signer, index) => (
          <li
            key={signer.boardMemberId}
            className="flex flex-col gap-2 rounded-lg border border-border-subtle p-12"
            data-testid={`minutes-approval-signer-${index}`}
          >
            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              {signer.name ?? fix49.unnamedOpening}
            </span>
            <span
              className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
              data-testid={`minutes-approval-signer-line-${index}`}
            >
              {askedSignerLine(signer)}
            </span>
            <AskedSignerDelivery signer={signer} index={index} stillUnjoined={stillUnjoined} />
          </li>
        ))}
      </ul>
      {request.notAsked.length > 0 ? (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="minutes-approval-not-asked"
        >
          {committee !== null
            ? cm.notAsked(request.notAsked, committee)
            : rc10.notAsked(request.notAsked)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * CBG-RC10 (not drawn): an officer records that one of the asked directors signed to approve these
 * minutes somewhere else. The server refuses a director not asked, a second signature on these bytes
 * and minutes that moved, each with its own sentence, shown verbatim; the entry names no officer
 * account as the signer, ever (§S16 C16), and its provenance is required.
 */
function ApprovalSignatureOnBehalfDialog({
  orgRef,
  meetingId,
  segmentId,
  committee,
  contentHash,
  signers,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  meetingId: string;
  segmentId: string;
  /** CBG-YR6: a committee meeting's committee — its members were there — or null for the board. */
  committee: string | null;
  /** ⚠ The hash from the read that rendered what the officer read. Never refetched here. */
  contentHash: string;
  /** The asked directors with no signature on the current bytes. */
  signers: readonly AskedSigner[];
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [seatId, setSeatId] = useState<string | null>(null);
  const [provenanceNote, setProvenanceNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: async () => {
      if (seatId === null) return;
      await operations.officerRecordBoardMinutesApprovalSignatureOnBehalf({
        path: { org_ref: orgRef, meeting_id: meetingId },
        query: { segment_id: segmentId },
        body: {
          board_member_id: seatId,
          content_hash: contentHash,
          provenance_note: provenanceNote.trim(),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Signature recorded.');
      onRecorded();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label={rc10.onBehalfTitle}
        className="flex flex-col gap-16"
        testId="minutes-approval-onbehalf-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {rc10.onBehalfTitle}
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {committee !== null ? cm.onBehalfBody : rc10.onBehalfBody}
        </p>

        <Select
          label={committee !== null ? cm.whichMember : rc10.whichDirector}
          appearance="saas"
          placeholder={committee !== null ? cm.chooseMember : rc10.chooseDirector}
          value={seatId}
          onValueChange={(next) => setSeatId(next)}
          options={signers.map((signer) => ({
            value: signer.boardMemberId,
            label: signer.name ?? fix49.unnamedOpening,
          }))}
          testId="minutes-approval-onbehalf-seat"
        />

        <Textarea
          label={rc10.whereFrom}
          value={provenanceNote}
          onChange={(event) => setProvenanceNote(event.target.value)}
          rows={3}
          testId="minutes-approval-onbehalf-provenance"
        />
        <FieldNote>
          Required. Say where the signature came from — a signed page you hold, an email, a call you
          took. This is what makes the entry evidence years later.
        </FieldNote>

        <div className="flex flex-col gap-6" data-testid="minutes-approval-onbehalf-fingerprint">
          <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {rc10.namesTheseBytes}
          </span>
          <Fingerprint value={contentHash} testId="minutes-approval-onbehalf-hash" />
        </div>

        {error !== null ? (
          <ErrorMessage testId="minutes-approval-onbehalf-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="minutes-approval-onbehalf-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={seatId === null || provenanceNote.trim().length === 0 || record.isPending}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="minutes-approval-onbehalf-save"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * CBG-RC10 (not drawn): withdrawing the request, with its required reason. The signatures already
 * given stay in the minutes' history and count for nothing; an officer may ask again.
 */
function WithdrawSignaturesDialog({
  orgRef,
  meetingId,
  segmentId,
  onClose,
  onWithdrawn,
}: {
  orgRef: string;
  meetingId: string;
  segmentId: string;
  onClose: () => void;
  onWithdrawn: () => void;
}) {
  const operations = useBoardOperations();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      await operations.officerWithdrawBoardMinutesApprovalSignatures({
        path: { org_ref: orgRef, meeting_id: meetingId },
        query: { segment_id: segmentId },
        body: { reason: reason.trim() },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(rc10.withdrawn);
      onWithdrawn();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t do that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !withdraw.isPending && onClose()}>
      <Dialog.Content
        aria-label={rc10.withdrawTitle}
        className="flex flex-col gap-16"
        testId="minutes-withdraw-signatures-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {rc10.withdrawTitle}
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {rc10.withdrawBody}
        </p>

        <Textarea
          label={rc10.withdrawWhy}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          testId="minutes-withdraw-signatures-reason"
        />

        {error !== null ? (
          <ErrorMessage testId="minutes-withdraw-signatures-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={withdraw.isPending}
            onClick={onClose}
            testId="minutes-withdraw-signatures-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={withdraw.isPending}
            disabled={reason.trim().length === 0 || withdraw.isPending}
            onClick={() => {
              setError(null);
              withdraw.mutate();
            }}
            testId="minutes-withdraw-signatures-confirm"
          >
            {rc10.withdrawConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
